import { getProvider } from './custom-installments-providers.js';

function formatMoneyRON(value) {
  // Shopify's {{ money }} Liquid filter matches the shop's `money_format`.
  // For JS live updates we use a locale-aware formatter. Default: "123 RON" with no decimals.
  if (!Number.isFinite(value)) return '—';
  const rounded = Math.round(value);
  return new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 0 }).format(rounded) + ' RON';
}

function resolvePriceRange(provider, banner) {
  const minOverride = parseFloat(banner.dataset.minOverride);
  const maxOverride = parseFloat(banner.dataset.maxOverride);
  return {
    min: Number.isFinite(minOverride) ? minOverride : provider.priceRange.min,
    max: Number.isFinite(maxOverride) ? maxOverride : provider.priceRange.max,
  };
}

/**
 * Pick the "headline" months for the banner teaser.
 * Rule: longest allowed period → lowest monthly rate → best merchandising.
 */
function pickHeadlineMonths(provider, price) {
  const allowed = provider.allowedMonthsFor(price);
  if (allowed.length === 0) return null;
  return allowed[allowed.length - 1];
}

/**
 * Returns structured teaser parts so the banner can highlight the
 * monthly rate visually (vs. the old single flat string).
 * Shape: { prefix?: string, rate: string, suffix: string } | null
 */
function teaserPartsFor(provider, price) {
  const months = pickHeadlineMonths(provider, price);
  if (months == null) return null;
  const result = provider.compute(price, months);
  if (!result.eligible) return null;

  const rate = `${formatMoneyRON(result.monthly)}/lună`;
  if (result.tier === 'quattro') {
    return { rate, suffix: `· ${months} rate · 0% dobândă` };
  }
  return { prefix: 'De la ', rate, suffix: `· în ${months} rate` };
}

class CustomInstallmentsBanner extends HTMLElement {
  constructor() {
    super();
    this._onVariantChange = this._onVariantChange.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this._provider = getProvider(this.dataset.provider);
    this._teaserEl = this.querySelector('.custom-installments-banner__teaser');
    this._buttonEl = this.querySelector('.custom-installments-banner__button');

    // Initial render
    const initialPrice = parseFloat(this.dataset.initialPrice);
    this._currentPrice = Number.isFinite(initialPrice) ? initialPrice : 0;
    this._render();

    // Listen for variant changes from the closest <product-info>
    this._productInfo = this.closest('product-info');
    if (this._productInfo) {
      this._productInfo.addEventListener('variant:change', this._onVariantChange);
      this._productInfo.addEventListener('variantchange', this._onVariantChange); // fallback alias
    }

    if (this._buttonEl) {
      this._buttonEl.addEventListener('click', this._onClick);
    }
  }

  disconnectedCallback() {
    if (this._productInfo) {
      this._productInfo.removeEventListener('variant:change', this._onVariantChange);
      this._productInfo.removeEventListener('variantchange', this._onVariantChange);
    }
    if (this._buttonEl) {
      this._buttonEl.removeEventListener('click', this._onClick);
    }
  }

  _onVariantChange(event) {
    // Hyper tends to pass variant info in event.detail.variant.price (in cents).
    // If not found, try re-reading the current price node in the DOM.
    let newPrice = null;
    const variant = event?.detail?.variant;
    if (variant && Number.isFinite(variant.price)) {
      newPrice = variant.price / 100;
    } else if (this._productInfo) {
      const priceNode = this._productInfo.querySelector('[data-product-price]');
      if (priceNode) {
        const attr = parseFloat(priceNode.getAttribute('data-product-price'));
        if (Number.isFinite(attr)) newPrice = attr;
      }
    }
    if (newPrice != null) {
      this._currentPrice = newPrice;
      this._render();

      // If the global modal is currently open for THIS banner's provider,
      // re-dispatch installments:open so it re-renders with the new price.
      const modal = document.querySelector('custom-installments-modal dialog');
      if (modal && modal.open && Number.isFinite(this._currentPrice)) {
        this.dispatchEvent(new CustomEvent('installments:open', {
          bubbles: true,
          detail: {
            providerId: this.dataset.provider,
            price: this._currentPrice,
            heading: this.dataset.heading,
            disclaimer: this.dataset.disclaimer,
            logoUrl: this.dataset.logoUrl,
            productTitle: this.dataset.productTitle,
          },
        }));
      }
    }
  }

  _onClick(event) {
    event.preventDefault();
    this.dispatchEvent(new CustomEvent('installments:open', {
      bubbles: true,
      detail: {
        providerId: this.dataset.provider,
        price: this._currentPrice,
        heading: this.dataset.heading,
        disclaimer: this.dataset.disclaimer,
        logoUrl: this.dataset.logoUrl,
        logoIsOverride: this.dataset.logoIsOverride === 'true',
        productTitle: this.dataset.productTitle,
        accentColor: this.dataset.accentColor,
      },
    }));
  }

  _render() {
    const price = this._currentPrice;
    const range = resolvePriceRange(this._provider, this);
    if (!Number.isFinite(price) || price < range.min || price > range.max) {
      this.hidden = true;
      return;
    }
    const parts = teaserPartsFor(this._provider, price);
    if (!parts) {
      this.hidden = true;
      return;
    }
    if (this._teaserEl) {
      this._teaserEl.replaceChildren();
      if (parts.prefix) this._teaserEl.append(parts.prefix);
      const rateEl = document.createElement('span');
      rateEl.className = 'custom-installments-banner__teaser-rate';
      rateEl.textContent = parts.rate;
      this._teaserEl.appendChild(rateEl);
      this._teaserEl.append(' ' + parts.suffix);
    }
    this.hidden = false;
  }
}

if (!customElements.get('custom-installments-banner')) {
  customElements.define('custom-installments-banner', CustomInstallmentsBanner);
}

class CustomInstallmentsModal extends HTMLElement {
  constructor() {
    super();
    this._onOpenRequest = this._onOpenRequest.bind(this);
    this._onChipClick = this._onChipClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
  }

  connectedCallback() {
    this._dialog = this.querySelector('dialog');
    this._providerPillEl = this.querySelector('[data-provider-pill]');
    this._titleEl = this.querySelector('.custom-installments-modal__title');
    this._productTitleEl = this.querySelector('[data-product-title]');
    this._heroTagEl = this.querySelector('[data-hero-tag]');
    this._heroSubEl = this.querySelector('[data-hero-sub]');
    this._chipsEl = this.querySelector('[data-chips]');
    this._monthlyEl = this.querySelector('[data-monthly]');
    this._aprEl = this.querySelector('[data-apr]');
    this._commissionEl = this.querySelector('[data-commission]');
    this._insuranceEl = this.querySelector('[data-insurance]');
    this._insuranceRow = this.querySelector('[data-insurance-row]');
    this._disclaimerEl = this.querySelector('[data-disclaimer]');
    this._closeBtn = this.querySelector('[data-close]');

    document.addEventListener('installments:open', this._onOpenRequest);
    if (this._closeBtn) this._closeBtn.addEventListener('click', this._onCloseClick);
    if (this._dialog) this._dialog.addEventListener('click', this._onBackdropClick);
    if (this._chipsEl) this._chipsEl.addEventListener('click', this._onChipClick);
  }

  disconnectedCallback() {
    document.removeEventListener('installments:open', this._onOpenRequest);
  }

  _onOpenRequest(event) {
    const detail = event.detail || {};
    this._provider = getProvider(detail.providerId);
    this._price = detail.price;
    this._heading = detail.heading || 'Calculează rata';
    this._logoUrl = detail.logoUrl;
    this._logoIsOverride = !!detail.logoIsOverride;
    this._disclaimer = detail.disclaimer || '';
    this._productTitle = detail.productTitle || '';
    if (this._dialog && detail.accentColor) {
      this._dialog.style.setProperty('--ci-accent', detail.accentColor);
    }

    // Pick a default month: longest allowed (same logic as banner teaser).
    const allowed = this._provider.allowedMonthsFor(this._price);
    this._selectedMonths = allowed.length > 0 ? allowed[allowed.length - 1] : null;

    this._renderStatic();
    this._renderChips();
    this._renderBreakdown();

    if (!this._dialog.open) this._dialog.showModal();
  }

  _renderStatic() {
    if (this._providerPillEl) {
      if (this._logoIsOverride && this._logoUrl) {
        this._providerPillEl.innerHTML = '';
        this._providerPillEl.classList.add('custom-installments-modal__provider-pill--logo');
        const img = document.createElement('img');
        img.src = this._logoUrl;
        img.alt = this._provider.name;
        img.className = 'custom-installments-modal__provider-logo';
        this._providerPillEl.appendChild(img);
      } else {
        this._providerPillEl.classList.remove('custom-installments-modal__provider-pill--logo');
        this._providerPillEl.textContent = this._provider.name;
      }
    }
    if (this._titleEl) this._titleEl.textContent = this._heading;
    if (this._productTitleEl) {
      this._productTitleEl.textContent = this._productTitle;
      this._productTitleEl.hidden = !this._productTitle;
    }
    if (this._disclaimerEl) this._disclaimerEl.textContent = this._disclaimer;
  }

  _renderChips() {
    if (!this._chipsEl) return;
    this._chipsEl.innerHTML = '';
    const allowed = this._provider.allowedMonthsFor(this._price);
    // Show all "commonly-offered" months as chips; disable those not allowed for this price.
    const allChipCounts = [4, 6, 9, 12, 18, 24, 36, 48, 60];
    for (const count of allChipCounts) {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'custom-installments-modal__chip';
      chip.setAttribute('role', 'radio');
      chip.dataset.months = String(count);
      chip.textContent = String(count);
      const isAllowed = allowed.includes(count);
      chip.setAttribute('aria-disabled', String(!isAllowed));
      if (!isAllowed) chip.disabled = true;
      chip.setAttribute('aria-checked', String(count === this._selectedMonths));
      this._chipsEl.appendChild(chip);
    }
  }

  _onChipClick(event) {
    const chip = event.target.closest('button[data-months]');
    if (!chip || chip.disabled) return;
    const months = parseInt(chip.dataset.months, 10);
    if (!Number.isFinite(months)) return;
    this._selectedMonths = months;
    // Update chip aria-checked states
    for (const el of this._chipsEl.querySelectorAll('button[data-months]')) {
      el.setAttribute('aria-checked', String(parseInt(el.dataset.months, 10) === months));
    }
    this._renderBreakdown();
  }

  _renderBreakdown() {
    if (this._selectedMonths == null) return;
    const r = this._provider.compute(this._price, this._selectedMonths);
    if (!r.eligible) return;
    if (this._heroTagEl) {
      this._heroTagEl.textContent = `Rata lunară · ${this._selectedMonths} rate`;
    }
    if (this._monthlyEl) this._monthlyEl.textContent = formatMoneyRON(r.monthly);
    if (this._heroSubEl) {
      this._heroSubEl.textContent =
        `Pentru un produs de ${formatMoneyRON(this._price)} · total ${formatMoneyRON(r.total)}`;
    }
    if (this._aprEl) this._aprEl.textContent = r.apr ? `${r.apr}%` : '0%';
    if (this._commissionEl) this._commissionEl.textContent = formatMoneyRON(r.commission);
    if (this._insuranceEl) {
      if (r.insurance > 0) {
        this._insuranceEl.textContent = formatMoneyRON(r.insurance);
      } else {
        this._insuranceEl.textContent = 'inclusă în rată';
      }
    }
    if (this._insuranceRow) {
      this._insuranceRow.hidden = r.tier === 'quattro';
    }
  }

  _onCloseClick() {
    if (this._dialog && this._dialog.open) this._dialog.close();
  }

  _onBackdropClick(event) {
    // Native <dialog> registers clicks on the backdrop as clicks on the dialog itself.
    // We distinguish by checking if the click target was the dialog element (not a child).
    if (event.target === this._dialog) this._dialog.close();
  }
}

if (!customElements.get('custom-installments-modal')) {
  customElements.define('custom-installments-modal', CustomInstallmentsModal);
}
